import { Modal, Form, Input, Button, Select } from "antd";
import { useRef, useState, useEffect, useMemo } from "react";
import Draggable from "react-draggable";

import { formItemLayout, inputLayout } from "../../../../constants/ApplicationConstants/FormConstants";
import CircularProgressBar from "../../../common/CircularProgressBar";
import Loading from "../../../common/Loading";
import FormInputRules from "../../../../util/FormInputRules";
import { General } from "../../../../util/General";
import styles from "../../index.module.less";
import { useReactDraggable } from "../../../../hooks/useReactDraggable";

import type FixMeLater from "../../../../shared/types/fixMeLater.type";
import useGetOneMasterList from "../../../../hooks/useGetOneMasterList";
import { ModuleAndIdMapping, Modules } from "../../../../constants/DatabaseConstants/MasterConstant";

type RoleMasterProps = {
  saving: boolean;
  addRecord: boolean;
  handleAddRecord: (value: boolean) => void;
  rowData: FixMeLater;
  handleClearRowIdAndActionType: () => void;
  handleSubmit: (data: FixMeLater) => void;
};

const RoleMaster = (props: RoleMasterProps) => {
  /* CONSTANTS */
  const [form] = Form.useForm();
  const formRef = useRef<FixMeLater>(null);

  /* STATES */
  const { loading: hierarchyLevelListLoading, masterList: hierarchyLevelMasterList } = useGetOneMasterList(ModuleAndIdMapping[Modules.ROLE_MASTER]);

  const [formFieldsLoadedToForm, setFormFieldsLoadedToForm] = useState(false);

  /* CUSTOM HOOKS */
  const { bounds, onStart } = useReactDraggable();

  /* HANDLERS */
  const isOnAddLoading = !props.addRecord;
  const isOnEditLoading = !!props.addRecord;

  useEffect(() => {
    if (!props.addRecord && !isOnEditLoading && !formFieldsLoadedToForm) {
      form.setFieldsValue({
        ...props.rowData,
        hierarchy_level: Number(props.rowData.hierarchy_level), 
      });
      setFormFieldsLoadedToForm(true);
    }
  }, [isOnEditLoading]);

  const handleFormSubmit = (values: any) => {
    const updatedValues = {
      ...values,
      hierarchy_level: Number(values.hierarchy_level), 
    };

    props.handleSubmit(updatedValues);
  };

  const sortedHierarchyLevels = useMemo(() => {
    let levels = hierarchyLevelMasterList || [];

    if (props.addRecord) {
      const maxLevel = Math.max(...levels.map(item => item.hierarchy_level));
      if (!levels.some(item => item.hierarchy_level === maxLevel + 1)) {
        levels.push({ hierarchy_level: maxLevel + 1 });
      }
    }

    const uniqueLevels = Array.from(new Set(levels.map(item => item.hierarchy_level)))
      .map(level => levels.find(item => item.hierarchy_level === level)!);

    return uniqueLevels
      .sort((a, b) => a.hierarchy_level - b.hierarchy_level)
      .map(item => ({
        label: `Level-${item.hierarchy_level}`,
        value: item.hierarchy_level, 
      }));
  }, [hierarchyLevelMasterList]);

  return (
    <Modal
      afterClose={() => form.resetFields()}
      destroyOnClose={true}
      maskClosable={false}
      centered
      title={<div className="modal-container">{`${props.addRecord ? "Add" : "Edit"} User Role Master`}</div>}
      open={true}
      onCancel={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}
      width={"50%"}
      okButtonProps={{
        style: {
          display: "none"
        }
      }}
      cancelButtonProps={{ style: { display: "none" } }}
      modalRender={(modal) => (
        <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
          {modal}
        </Draggable>
      )}
    >
      {((props.addRecord && isOnAddLoading) || (!props.addRecord && !formFieldsLoadedToForm)) && (
        <div className={`${styles["textCenter"]}`}>
          <Loading />
        </div>
      )}

      {((props.addRecord && !isOnAddLoading) || (!props.addRecord && formFieldsLoadedToForm)) && (
        <Form
          ref={formRef}
          onValuesChange={(changedValues) => {
            const fieldName = Object.keys(changedValues)[0];
            form.validateFields([fieldName]);
          }}
          className={`${styles["formDiv"]}`}
          {...formItemLayout}
          form={form}
          validateTrigger="onBlur"
          colon={false}
          name="register"
          onFinish={handleFormSubmit}
        >
          <Form.Item {...inputLayout} name="rolename" label="Role Name" normalize={General.inputWhiteSpaceRemove} rules={[FormInputRules.requiredFieldInputRule("role name"), FormInputRules.ValidateFirstAndLastName()]}>
            <Input name="rolename" />
          </Form.Item>
          <Form.Item {...inputLayout} name="description" label="Description" normalize={General.inputWhiteSpaceRemove}>
            <Input name="description" />
          </Form.Item>

          <Form.Item {...inputLayout} name="hierarchy_level" label="Hierarchy Level" rules={[FormInputRules.requiredFieldInputRule("hierarchy level")]}>
            <Select
              placeholder="Select Level"
              value={props.rowData?.hierarchy_level ? Number(props.rowData.hierarchy_level) : undefined} 
              onChange={(value) => form.setFieldsValue({ hierarchy_level: value })}
              loading={hierarchyLevelListLoading}
              options={sortedHierarchyLevels}
            />
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} htmlType="submit">
              {props.saving ? <CircularProgressBar /> : props.addRecord ? "Submit" : "Update"}
            </Button>
          </Form.Item>

          <Form.Item className={`${styles["form-itemFloatR20"]}`}>
            <Button disabled={props.saving} className={`gx-mb-0 button-gradiant ${styles["button-m01"]}`} onClick={props.addRecord ? props.handleAddRecord.bind(this, false) : props.handleClearRowIdAndActionType}>
              Close
            </Button>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default RoleMaster;


